import React, { useEffect, useRef, useState } from 'react';

/**
 * LazySection
 *
 * Defers mounting its children until the placeholder enters the viewport
 * (or within `rootMargin` px below it). This breaks the "everything renders
 * on mount" pattern that triggers all below-the-fold API calls at once on
 * the homepage, freeing the main thread for first paint.
 *
 * The placeholder reserves space via `minHeight` so the page doesn't jump
 * when the real content swaps in.
 *
 * Props:
 *   - minHeight: number | string — vertical space reserved before mount (default 600)
 *   - rootMargin: string — IntersectionObserver rootMargin (default "300px")
 *   - children: any — content to mount once in view
 */
const LazySection = ({ children, minHeight = 600, rootMargin = '300px' }) => {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    const el = ref.current;
    if (!el) return;

    // If IntersectionObserver isn't supported, mount immediately so we
    // never silently hide content.
    if (typeof IntersectionObserver === 'undefined') {
      setMounted(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setMounted(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin, threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted, rootMargin]);

  if (mounted) return children;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={{ minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight }}
    />
  );
};

export default LazySection;
